import React from 'react';

import type {
  ContractBlock,
  ContractDocument,
  ContractSlot,
} from '../api/contract';
import { W2_CONTRACT_TID } from '../constants/testids';

const TID = W2_CONTRACT_TID.document;

/** What the prospect has typed so far, keyed by the slot the server marked. */
export type ContractSlotValues = Partial<Record<ContractSlot, string>>;

interface ContractDocumentViewProps {
  document: ContractDocument;
  slotValues: ContractSlotValues;
}

const DASH = '—';

/** The paper form pairs each of these onto the line of the field above it. */
const PAIRED_WITH_PREVIOUS = new Set(['Phone #', 'Fax #']);

/** A clause carrying its own number in the text, e.g. `4. Member agrees …`. */
const NUMBERED_CLAUSE = /^(\d+)\.\s+/;

/** The guideline bands in section 12's instructions, e.g. `1-5 …`, `50+ …`. */
const GUIDELINE_BAND = /^\d+\s*[-–]\s*\d+\s|^\d+\+\s/;

/**
 * A field as the paper form draws it: label, then the value written on a rule. An empty
 * value keeps the rule and centres a dash, so the form still shows the line was left blank.
 */
const Field: React.FC<{
  label: string;
  value: string | null;
  placeholder?: string;
}> = ({ label, value, placeholder }) => (
  <div className="flex flex-wrap items-end my-[7px]">
    <span className="pr-2 text-[14px] text-[#5a5a70] sm:whitespace-nowrap">
      {label}
    </span>
    <span
      className={`min-h-5 min-w-[140px] flex-1 break-all border-b border-[#c9c9d4] px-1.5 pb-px ${
        value
          ? 'font-semibold text-[#1a1a2e]'
          : placeholder
            ? 'italic text-[#b3b3bf]'
            : 'text-center text-[#8a8aa0]'
      }`}
    >
      {value ?? placeholder ?? DASH}
    </span>
  </div>
);

const Checkbox: React.FC<{ checked?: boolean }> = ({ checked = true }) => (
  <span
    aria-hidden="true"
    className="mr-2 inline-flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border-[1.5px] border-[#33334a] bg-white text-[11px] font-bold leading-none"
  >
    {checked ? 'X' : ''}
  </span>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-1.5 text-[12px] leading-5 text-[#8a8aa0]">{children}</p>
);

const Legal: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="my-[7px] text-[13.5px] leading-[1.55] text-[#33334a]">
    {children}
  </p>
);

/** Groups a field list into the form's lines: a phone/fax rides with the field above it. */
const fieldLines = <T extends { label: string }>(rows: T[]): T[][] =>
  rows.reduce<T[][]>((lines, row) => {
    const last = lines[lines.length - 1];
    if (PAIRED_WITH_PREVIOUS.has(row.label) && last && last.length === 1) {
      last.push(row);
    } else {
      lines.push([row]);
    }
    return lines;
  }, []);

/**
 * Consecutive paragraphs that carry their own clause number become one numbered list;
 * the plan-count guideline bands become a bullet list. Everything else is prose.
 */
type ProseRun =
  | { list: false; text: string }
  | { list: true; ordered: boolean; start: number; items: string[] };

const proseRuns = (texts: string[]): ProseRun[] =>
  texts.reduce<ProseRun[]>((runs, text) => {
    const numbered = NUMBERED_CLAUSE.exec(text);
    const open = runs[runs.length - 1];
    if (numbered) {
      const item = text.slice(numbered[0].length);
      if (open?.list && open.ordered) {
        open.items.push(item);
      } else {
        runs.push({
          list: true,
          ordered: true,
          start: Number(numbered[1]),
          items: [item],
        });
      }
    } else if (GUIDELINE_BAND.test(text)) {
      if (open?.list && !open.ordered) {
        open.items.push(text);
      } else {
        runs.push({ list: true, ordered: false, start: 1, items: [text] });
      }
    } else {
      runs.push({ list: false, text });
    }
    return runs;
  }, []);

const Prose: React.FC<{ texts: string[] }> = ({ texts }) => (
  <>
    {proseRuns(texts).map((run, i) =>
      run.list ? (
        run.ordered ? (
          <ol
            key={i}
            start={run.start}
            className="my-1.5 ml-6 list-decimal text-[13.5px] leading-[1.55] text-[#33334a]"
          >
            {run.items.map((item, j) => (
              <li key={j} className="my-1">
                {item}
              </li>
            ))}
          </ol>
        ) : (
          <ul
            key={i}
            className="my-1.5 ml-10 list-disc text-[13.5px] leading-[1.55] text-[#33334a]"
          >
            {run.items.map((item, j) => (
              <li key={j} className="my-1">
                {item}
              </li>
            ))}
          </ul>
        )
      ) : (
        <Legal key={i}>{run.text}</Legal>
      )
    )}
  </>
);

/**
 * One block, rendered by kind. This switch IS the render allowlist: a block kind it does
 * not name renders nothing at all.
 */
const Block: React.FC<{
  block: ContractBlock;
  sectionNumber: number;
  slotValues: ContractSlotValues;
}> = ({ block, sectionNumber, slotValues }) => {
  switch (block.kind) {
    case 'fields':
      return (
        <div className={sectionNumber === 4 ? 'max-w-[380px]' : undefined}>
          {fieldLines(block.rows).map((line, i) => (
            <div
              key={i}
              className={line.length > 1 ? 'flex flex-wrap gap-5' : undefined}
            >
              {line.map((row, j) => (
                <div key={`${row.label}-${j}`} className="flex-1 basis-65">
                  <Field
                    label={row.label}
                    value={
                      (row.slot ? slotValues[row.slot] : row.value) || null
                    }
                    placeholder={
                      row.slot ? '(enter below when signing)' : undefined
                    }
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      );

    case 'paragraphs':
      return <Prose texts={block.text} />;

    case 'marks':
      // The waiting-period options stack one per line, each a choice of its own; the
      // single-sentence elections read inline with their sentence.
      return block.items.length > 1 && sectionNumber === 12 ? (
        <div className="ml-2 mt-1.5 text-[14px] leading-[1.9] text-[#1a1a2e]">
          {block.items.map((item, i) => (
            <div key={`${item.label}-${i}`}>
              <Checkbox checked={item.checked} />
              {item.checked ? <b>{item.label}</b> : item.label}
            </div>
          ))}
        </div>
      ) : (
        <div className="my-[7px] text-[14px] text-[#1a1a2e]">
          {block.items.map((item, i) => (
            <span key={`${item.label}-${i}`} className="mr-6 inline-block">
              <Checkbox checked={item.checked} />
              {item.label}
            </span>
          ))}
        </div>
      );

    case 'statement':
      return <Note>{block.text}</Note>;

    case 'contributionGrid':
      return (
        <div className="mt-2 overflow-x-auto">
          <div className="text-[14px]">Employer Contribution:</div>
          <table className="ml-1.5 my-2 border-collapse text-[14px]">
            <thead>
              <tr className="text-[13px] font-semibold text-[#5a5a70]">
                <th className="py-1.5 pr-6 text-left" />
                <th className="py-1.5 pr-6 text-left">Percentage</th>
                <th className="py-1.5 pr-6 text-left">Flat Rate</th>
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row.tierLabel}>
                  <td className="py-1.5 pr-6">{row.tierLabel}</td>
                  <td className="py-1.5 pr-6">{row.percentage ?? DASH}</td>
                  <td className="py-1.5 pr-6">
                    <span className="inline-block min-w-19 border-b border-[#c9c9d4] px-2.5 pb-px text-center font-semibold">
                      {row.flatRate}
                    </span>
                    {row.derived && '*'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {block.footnote && <Note>* {block.footnote}</Note>}
        </div>
      );

    case 'planList':
      return (
        <div className="overflow-x-auto">
          <table className="my-2 w-full border-collapse text-[14px]">
            <tbody>
              {block.plans.map((plan, i) => (
                <tr key={`${plan.name}-${i}`}>
                  <td className="w-6.5 p-1.5">
                    <Checkbox />
                  </td>
                  <td className="border-b border-[#d9d9e2] p-1.5 font-semibold text-[#1a1a2e]">
                    {plan.name}
                  </td>
                  <td className="border-b border-[#d9d9e2] p-1.5 text-[#5a5a70]">
                    {plan.deductibleLine ?? DASH}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Note>{block.note}</Note>
        </div>
      );

    case 'signature':
      return (
        <div>
          {block.attestation.map((paragraph, i) => (
            <Legal key={i}>{paragraph}</Legal>
          ))}
          <div className="mt-4">
            <Field
              label="Authorized Representative of Member (Name)"
              value={slotValues.signerName || null}
              placeholder="(sign below)"
            />
            <div className="flex flex-wrap gap-5">
              <div className="flex-1 basis-65">
                <Field
                  label="Authorized Representative’s Signature"
                  value={slotValues.signatureName || null}
                  placeholder="(sign below)"
                />
              </div>
              <div className="basis-50">
                <Field label="Date" value={block.dateValue} />
              </div>
            </div>
            <Field label="Broker Representative:" value={block.brokerName} />
          </div>
        </div>
      );
  }
};

/**
 * The contract document, rendered from the server's blocks — sections 1 to 13, in the order
 * the server gave them, inside the panel that makes it read as the paper form it becomes.
 * There is deliberately NO section text in this file: the wording, ordering and every static
 * mark come from the same builder the executed PDF renders, so the page cannot show a
 * document different from the one that gets filed.
 *
 * The two values the signer types are live-filled into their slots as they type; they exist
 * only in this component's props and the sign request, never in the resolve payload.
 */
export const ContractDocumentView: React.FC<ContractDocumentViewProps> = ({
  document,
  slotValues,
}) => (
  <section
    data-testid={TID.root}
    className="rounded-xl border border-[#ececf2] bg-[#fafafb] px-3.5 py-4.5 font-[Arial,Helvetica,sans-serif] sm:px-8.5 sm:py-7.5"
  >
    {/* The programme's wordmarks, as the printed form draws them — styled text, no assets. */}
    <div className="mb-4.5 text-center">
      <div>
        <span className="mr-4.5 text-[13px] font-bold tracking-[3px] text-[#23315e]">
          ▲ TRIAD
        </span>
        <span className="text-[20px] font-bold -tracking-[0.5px] text-[#8e1a4d]">
          onehealth
        </span>
      </div>
      <h2 className="mt-2 text-[16px] font-bold text-[#1a1a2e]">
        Health Program Participation
      </h2>
      <div className="text-[13.5px] text-[#5a5a70]">Request/Contract</div>
    </div>

    {document.sections.map((section) => (
      <div key={section.number} data-testid={TID.section(section.number)}>
        <h3 className="mb-2 mt-5.5 text-[15px] font-bold text-[#1a1a2e]">
          <u>Section {section.number}</u>{' '}
          <span className="font-normal">
            {section.heading}
            {section.subheading && <i> {section.subheading}</i>}
          </span>
        </h3>
        {section.blocks.map((block, i) => (
          <Block
            key={i}
            block={block}
            sectionNumber={section.number}
            slotValues={slotValues}
          />
        ))}
      </div>
    ))}

    {document.termsVersion && (
      <p
        data-testid={TID.termsVersion}
        className="mt-6.5 border-t border-[#ececf2] pt-2.5 text-[11.5px] text-[#8a8aa0]"
      >
        {document.termsVersion}
      </p>
    )}
  </section>
);
